import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Download } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import StatusPill from "@/components/StatusPill";
import { formatDateTime } from "@/lib/utils";
import { ParameterReviewTable, RawDataSourceReview } from "@/components/jobs/ParameterReviewTable";

export default function JobDetailPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);

  const load = () => api.get(`/jobs/${id}`).then(({ data }) => setJob(data));
  useEffect(() => { load(); }, [id]);

  const resubmit = async () => {
    try {
      await api.post(`/jobs/${id}/resubmit`);
      toast.success("Job resubmitted for review.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resubmit");
    }
  };

  if (!job) return <LoadingScreen />;

  return (
    <div className="space-y-6">
      <Link to="/app/jobs" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft size={14} /> Back to jobs
      </Link>

      <div className="sqit-card p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-widest">Job</div>
            <h1 className="mt-1 font-display text-3xl font-bold text-gray-900">{job.job_code}</h1>
            <p className="mt-1 text-gray-600">{job.project_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={job.status} />
            {job.status === "revision" && (
              <button onClick={resubmit} data-testid="job-resubmit-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0B6936] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f]">
                <RefreshCw size={14} /> Resubmit
              </button>
            )}
          </div>
        </div>

        <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["Module", job.module_name],
            ["Samples", job.number_of_samples],
            ["Credits", `${job.credits_required} required`],
            ["Frozen", job.credits_frozen],
            ["Consumed", job.credits_consumed],
            ["Submitted", formatDateTime(job.created_at)],
            ["Last update", formatDateTime(job.updated_at)],
            ["Assignee", job.assignee_name || "—"],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-gray-500 uppercase tracking-widest">{k}</dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">{v ?? "—"}</dd>
            </div>
          ))}
        </dl>

        {job.short_notes && (
          <div className="mt-6 p-4 bg-[#F8FAF7] border border-[#E5E7EB] rounded-lg">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Notes</div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.short_notes}</p>
          </div>
        )}
      </div>

      <div className="sqit-card p-8">
        <h3 className="font-display text-lg font-semibold text-gray-900">Submitted details</h3>
        <div className="mt-6 space-y-6">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Project details</div>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-widest">Project name</dt>
                <dd className="mt-1 text-sm text-gray-900">{job.project_name || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-widest">Sample count</dt>
                <dd className="mt-1 text-sm text-gray-900">{job.number_of_samples || "—"}</dd>
              </div>
            </dl>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Raw Data Source</div>
            <RawDataSourceReview source={job.raw_data_source} legacyUrls={job.sample_urls} />
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Parameters</div>
            <ParameterReviewTable parameters={job.parameters} emptyMessage="No module parameters were submitted." />
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="sqit-card p-8">
        <h3 className="font-display text-lg font-semibold text-gray-900">Activity timeline</h3>
        <ul className="mt-6 space-y-4">
          {(job.history || []).slice().reverse().map((h, i) => (
            <li key={i} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-[#0B6936]" />
                {i < job.history.length - 1 && <div className="flex-1 w-px bg-gray-200 my-1" />}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2">
                  <StatusPill status={h.status} />
                  <span className="text-xs text-gray-500">{formatDateTime(h.at)}</span>
                </div>
                {h.note && <p className="mt-2 text-sm text-gray-700">{h.note}</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Sample URLs */}
      {/* <div className="sqit-card p-8">
        <h3 className="font-display text-lg font-semibold text-gray-900">Sample URLs</h3>
        <ul className="mt-4 divide-y divide-[#E5E7EB]">
          {job.sample_urls?.map((u, i) => (
            <li key={i} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="text-xs font-semibold text-gray-500 uppercase w-8">S{String(i + 1).padStart(2, "0")}</div>
                <div className="text-sm text-gray-700 truncate">{u}</div>
              </div>
              <a href={u} target="_blank" rel="noreferrer noopener"
                className="text-[#538135] hover:text-[#09582D] flex-shrink-0">
                <ExternalLink size={14} />
              </a>
            </li>
          ))}
        </ul>
      </div> */}

      {/* Results */}
      {job.result && (
        <div className="sqit-card p-8">
          <h3 className="font-display text-lg font-semibold text-gray-900">Results</h3>
          {job.result.report_url && (
            <a href={job.result.report_url} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f]">
              <Download size={14} /> Download report
            </a>
          )}
          {(job.result.file_urls || []).length > 0 && (
            <ul className="mt-4 divide-y divide-[#E5E7EB]">
              {job.result.file_urls.map((f, i) => (
                <li key={i} className="flex items-center justify-between py-3">
                  <span className="text-sm text-gray-700">{f.name}</span>
                  <a href={f.url} className="inline-flex items-center gap-1 text-sm font-medium text-[#0B6936] transition-colors hover:text-[#07522f]">
                    <Download size={14} /> Download
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
